import React, { useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import { API } from '../../contexts/AuthContext';
import Tooltip from '../Tooltip';
import { ROXO, ACOES, ACAO, ETAPAS, hoje, somaDias } from './demandasUtil';

// "O que esta demanda vai fazer" + o botão Gerar de cada ação.
// Nada é gerado sozinho: cada ação só vira campanha / land / demanda no quadro quando alguém clica em Gerar
// e confirma os dados (nome, período, prazo) no diálogo.

const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
const rotulo = (a) => (a.acao === 'outro' ? a.titulo : ACAO[a.acao]?.label || a.acao);

export default function AcoesDemanda({ edit, setEdit, campanhas, onGerado, onAbrirDemanda }) {
  const navigate = useNavigate();
  const [outra, setOutra] = useState('');
  const [gerando, setGerando] = useState(null);
  const acoes = edit.acoes || [];
  const tem = (v) => acoes.some(a => a.acao === v);
  const nomeCampanha = campanhas.find(c => String(c.id) === String(edit.campanha_id))?.nome;

  const toggle = (v) => setEdit(p => {
    const lista = p.acoes || [];
    const atual = lista.find(a => a.acao === v);
    if (atual?.ref_id) return p; // já gerada: não sai da lista
    return { ...p, acoes: atual ? lista.filter(a => a !== atual) : [...lista, { acao: v }] };
  });
  const remover = (a) => setEdit(p => ({ ...p, acoes: (p.acoes || []).filter(x => x !== a) }));
  const addOutra = () => {
    const t = outra.trim();
    if (!t) return;
    setEdit(p => ({ ...p, acoes: [...(p.acoes || []), { acao: 'outro', titulo: t.slice(0, 200) }] }));
    setOutra('');
  };

  async function gerar(a) {
    const cat = ACAO[a.acao];
    const gera = cat.gera || 'demanda';
    let corpo;
    if (gera === 'campanha' || gera === 'land') {
      const r = await Swal.fire({
        title: gera === 'campanha' ? 'Gerar campanha' : 'Gerar landpage',
        html: `
          <div class="text-start small">
            <p class="text-muted mb-2">${gera === 'campanha'
              ? 'A campanha é criada em <b>rascunho</b>, com as lojas desta demanda. Produtos e preços seguem na tela de Campanhas.'
              : 'Cria o cadastro da land em <b>rascunho</b>. O conteúdo (upload, editor ou IA) segue na tela Land.'} Confira o período:</p>
            <label class="form-label fw-bold mb-1">Nome</label>
            <input id="g-nome" class="form-control mb-2" maxlength="200" value="${esc(edit.titulo)}">
            <div class="row g-2">
              <div class="col-6"><label class="form-label fw-bold mb-1">Início</label><input id="g-ini" type="date" class="form-control" value="${esc(edit.data_inicio)}"></div>
              <div class="col-6"><label class="form-label fw-bold mb-1">Fim</label><input id="g-fim" type="date" class="form-control" value="${esc(edit.data_fim || edit.data_inicio)}"></div>
            </div>
          </div>`,
        showCancelButton: true, confirmButtonText: 'Gerar', cancelButtonText: 'Cancelar', confirmButtonColor: ROXO, focusConfirm: false,
        preConfirm: () => {
          const nome = document.getElementById('g-nome').value.trim();
          const ini = document.getElementById('g-ini').value;
          const fim = document.getElementById('g-fim').value;
          if (!nome) return Swal.showValidationMessage('Informe o nome');
          if (!ini || !fim) return Swal.showValidationMessage('Informe o início e o fim do período');
          if (fim < ini) return Swal.showValidationMessage('O fim não pode ser antes do início');
          return { nome, data_inicio: ini, data_fim: fim };
        },
      });
      if (!r.isConfirmed) return;
      corpo = r.value;
    } else {
      // prazo sugerido: 1 semana antes do início do período (ou o prazo da demanda)
      const sugerido = edit.data_inicio ? somaDias(edit.data_inicio, -7) : (edit.prazo || '');
      const r = await Swal.fire({
        title: `Gerar: ${esc(rotulo(a))}`,
        html: `
          <div class="text-start small">
            <p class="text-muted mb-2">Cria uma demanda no quadro (coluna <b>A fazer</b>), com as mesmas lojas${edit.campanha_id ? ' e a mesma campanha' : ''}.</p>
            <label class="form-label fw-bold mb-1">Título</label>
            <input id="g-tit" class="form-control mb-2" maxlength="200" value="${esc(`${rotulo(a)} — ${edit.titulo}`)}">
            <label class="form-label fw-bold mb-1">Prazo</label>
            <input id="g-prazo" type="date" class="form-control" value="${esc(sugerido)}">
            ${sugerido && sugerido < hoje() ? '<div class="text-danger mt-1">Atenção: o prazo sugerido já passou.</div>' : ''}
          </div>`,
        showCancelButton: true, confirmButtonText: 'Gerar', cancelButtonText: 'Cancelar', confirmButtonColor: ROXO, focusConfirm: false,
        preConfirm: () => {
          const titulo = document.getElementById('g-tit').value.trim();
          if (!titulo) return Swal.showValidationMessage('Informe o título');
          return { titulo, prazo: document.getElementById('g-prazo').value || null };
        },
      });
      if (!r.isConfirmed) return;
      corpo = r.value;
    }
    setGerando(a.id);
    try {
      await axios.post(`${API}/demandas/${edit.id}/acoes/${a.id}/gerar`, corpo);
      Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: `${rotulo(a)} gerada`, showConfirmButton: false, timer: 2500 });
      await onGerado();
    } catch (err) {
      Swal.fire('Erro', err.response?.data?.error || 'Não foi possível gerar a ação', 'error');
    } finally { setGerando(null); }
  }

  function abrirGerado(a) {
    if (a.ref_tipo === 'campanha') navigate(`/campanhas/${a.ref_id}/produtos`);
    else if (a.ref_tipo === 'land') navigate('/land');
    else if (a.ref_tipo === 'demanda') onAbrirDemanda(a.ref_id);
  }

  function situacao(a) {
    const cat = ACAO[a.acao] || {};
    if (a.ref_id) {
      const e = a.ref_tipo === 'demanda' ? ETAPAS[a.ref_status] : null;
      return (
        <div className="d-flex align-items-center gap-1 small">
          <i className="bi bi-check-circle-fill text-success" />
          <button type="button" className="btn btn-link btn-sm p-0 text-truncate" style={{ maxWidth: 220, color: ROXO }} onClick={() => abrirGerado(a)} title={a.ref_nome}>
            {a.ref_tipo === 'campanha' ? 'Campanha' : a.ref_tipo === 'land' ? 'Land' : 'Demanda'} #{a.ref_id}
          </button>
          {e && <span className="badge" style={{ background: e.bg, color: e.cor }}>{e.label}</span>}
          {a.ref_tipo !== 'demanda' && a.ref_status && <span className="badge bg-light text-muted border">{a.ref_status}</span>}
        </div>
      );
    }
    if (a.acao === 'campanha' && edit.campanha_id) {
      return <span className="small text-muted"><i className="bi bi-link-45deg me-1" />Ligada a {nomeCampanha || `#${edit.campanha_id}`}</span>;
    }
    if (!a.id || !edit.id) return <span className="small text-muted fst-italic">salve a demanda pra gerar</span>;
    if (cat.precisaCampanha && !edit.campanha_id) {
      return (
        <Tooltip texto="Gere a campanha desta demanda (ou escolha uma no campo Campanha e salve) antes">
          <span><button type="button" className="btn btn-sm btn-outline-secondary" disabled><i className="bi bi-lightning me-1" />Gerar</button></span>
        </Tooltip>
      );
    }
    return (
      <button type="button" className="btn btn-sm btn-primary-mkt" disabled={gerando === a.id} onClick={() => gerar(a)}>
        {gerando === a.id ? <span className="spinner-border spinner-border-sm" /> : <><i className="bi bi-lightning me-1" />Gerar</>}
      </button>
    );
  }

  return (
    <div className="mb-3 p-2 rounded-3" style={{ background: '#faf6f9', border: '1px solid #eadbe5' }}>
      <label className="form-label small fw-bold mb-1"><i className="bi bi-lightning-charge me-1" style={{ color: ROXO }} />O que esta demanda vai fazer <span className="text-muted fw-normal">(opcional — cada ação só é criada quando você clicar em Gerar)</span></label>
      <div className="d-flex flex-wrap gap-1 mb-2">
        {ACOES.map(c => {
          const on = tem(c.v);
          const travada = acoes.find(a => a.acao === c.v)?.ref_id;
          return (
            <button key={c.v} type="button" onClick={() => toggle(c.v)} className="btn btn-sm" disabled={!!travada}
              style={on ? { background: ROXO, color: '#fff' } : { border: '1px solid #ddd', color: '#555', background: '#fff' }}>
              <i className={`bi ${on ? 'bi-check-lg' : c.icon} me-1`} />{c.label}
            </button>
          );
        })}
      </div>
      <div className="input-group input-group-sm mb-2" style={{ maxWidth: 420 }}>
        <input className="form-control" placeholder="Outra ação — ex: Faixa na fachada" value={outra} maxLength={200}
          onChange={e => setOutra(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addOutra(); } }} />
        <button type="button" className="btn btn-outline-mkt" onClick={addOutra} disabled={!outra.trim()}><i className="bi bi-plus-lg" /></button>
      </div>

      {acoes.length > 0 && (
        <div className="bg-white rounded border">
          {acoes.map((a, i) => (
            <div key={a.id || `n${i}`} className="d-flex flex-wrap align-items-center justify-content-between gap-2 px-2 py-1" style={{ borderTop: i ? '1px solid #eee' : 'none' }}>
              <span className="small">
                <i className={`bi ${ACAO[a.acao]?.icon || 'bi-dot'} me-1`} style={{ color: ROXO }} />{rotulo(a)}
                {!a.ref_id && <button type="button" className="btn btn-link btn-sm p-0 ms-2 text-muted" title="Tirar da lista" onClick={() => remover(a)}><i className="bi bi-x" /></button>}
              </span>
              {situacao(a)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
